/**
 * Curated suggestions and vocabulary examples for STOP game categories.
 * Provides high-quality educational examples when a player leaves a category blank,
 * and aids in typo detection.
 */

export const CATEGORY_VOCABULARY: Record<string, Record<string, string[]>> = {
  animal: {
    A: ['Alligator', 'Ant', 'Ape', 'Armadillo', 'Albatross'],
    B: ['Bear', 'Butterfly', 'Bee', 'Bat', 'Beaver', 'Badger'],
    C: ['Cat', 'Camel', 'Cheetah', 'Cow', 'Chimpanzee', 'Crab'],
    D: ['Dog', 'Dolphin', 'Duck', 'Deer', 'Donkey', 'Dragonfly'],
    E: ['Elephant', 'Eagle', 'Eel', 'Elk', 'Emu'],
    F: ['Fox', 'Frog', 'Flamingo', 'Falcon', 'Fish', 'Ferret'],
    G: ['Giraffe', 'Gorilla', 'Goat', 'Goose', 'Gazelle'],
    H: ['Horse', 'Hippo', 'Hawk', 'Hedgehog', 'Hamster', 'Hyena'],
    I: ['Iguana', 'Impala', 'Insect', 'Ibex'],
    J: ['Jaguar', 'Jellyfish', 'Jackal', 'Jay'],
    K: ['Kangaroo', 'Koala', 'Kingfisher', 'Kiwi'],
    L: ['Lion', 'Leopard', 'Lizard', 'Llama', 'Lobster'],
    M: ['Monkey', 'Mouse', 'Moose', 'Meerkat', 'Mole'],
    N: ['Newt', 'Nightingale', 'Narwhal'],
    O: ['Owl', 'Octopus', 'Otter', 'Ostrich', 'Oryx'],
    P: ['Penguin', 'Panda', 'Parrot', 'Pig', 'Panther', 'Pigeon'],
    R: ['Rabbit', 'Rhino', 'Raccoon', 'Raven', 'Reindeer'],
    S: ['Snake', 'Shark', 'Sheep', 'Squirrel', 'Swan', 'Seal'],
    T: ['Tiger', 'Turtle', 'Toucan', 'Turkey', 'Toad'],
    U: ['Urchin', 'Umbrellabird'],
    V: ['Vulture', 'Viper', 'Vicuna'],
    W: ['Wolf', 'Whale', 'Walrus', 'Woodpecker', 'Wombat']
  },
  food: {
    A: ['Apple', 'Avocado', 'Almond', 'Apricot', 'Asparagus'],
    B: ['Banana', 'Bread', 'Burger', 'Broccoli', 'Blueberry', 'Bacon'],
    C: ['Carrot', 'Cheese', 'Cake', 'Chicken', 'Chocolate', 'Coffee'],
    D: ['Donut', 'Dumpling', 'Duck', 'Date', 'Dragonfruit'],
    E: ['Egg', 'Eggplant', 'Enchilada', 'Éclair'],
    F: ['Fish', 'Fries', 'Fig', 'Fajita', 'Fruit'],
    G: ['Grapes', 'Garlic', 'Grapefruit', 'Ginger', 'Guava'],
    H: ['Hamburger', 'Honey', 'Ham', 'Hotdog', 'Hummus'],
    I: ['Ice Cream', 'Iced Tea'],
    J: ['Jam', 'Jelly', 'Juice', 'Jalapeno'],
    K: ['Kiwi', 'Kebab', 'Kale', 'Ketchup'],
    L: ['Lemon', 'Lettuce', 'Lasagna', 'Lime', 'Lobster'],
    M: ['Mango', 'Milk', 'Mushroom', 'Muffin', 'Melon'],
    N: ['Noodles', 'Nut', 'Nachos', 'Nectarine'],
    O: ['Orange', 'Onion', 'Oatmeal', 'Olive', 'Omelette'],
    P: ['Pizza', 'Pasta', 'Potato', 'Peach', 'Pear', 'Pancake'],
    R: ['Rice', 'Radish', 'Raspberry', 'Ravioli', 'Ribs'],
    S: ['Salad', 'Soup', 'Sandwich', 'Strawberry', 'Steak', 'Sushi'],
    T: ['Tomato', 'Taco', 'Toast', 'Tea', 'Tuna', 'Turkey'],
    U: ['Udon', 'Upside-down cake'],
    V: ['Vanilla', 'Vegetables', 'Vinegar'],
    W: ['Watermelon', 'Waffle', 'Walnut', 'Wine', 'Wheat']
  },
  country_city: {
    A: ['Argentina', 'Amsterdam', 'Australia', 'Athens', 'Austria', 'Atlanta'],
    B: ['Brazil', 'Berlin', 'Belgium', 'Boston', 'Barcelona', 'Beijing'],
    C: ['Canada', 'Chicago', 'China', 'Cairo', 'Colombia', 'Copenhagen'],
    D: ['Denmark', 'Dublin', 'Dubai', 'Dallas', 'Denver'],
    E: ['Egypt', 'Edinburgh', 'Ecuador', 'England', 'Ethiopia'],
    F: ['France', 'Finland', 'Florence', 'Frankfurt'],
    G: ['Germany', 'Greece', 'Geneva', 'Guatemala'],
    H: ['Havana', 'Helsinki', 'Houston', 'Hungary', 'Hong Kong'],
    I: ['Italy', 'India', 'Iceland', 'Ireland', 'Istanbul', 'Indonesia'],
    J: ['Japan', 'Jamaica', 'Jerusalem', 'Jordan'],
    K: ['Kenya', 'Kyoto', 'Kuwait', 'Kiev'],
    L: ['London', 'Lisbon', 'Los Angeles', 'Lima', 'Luxembourg'],
    M: ['Madrid', 'Mexico', 'Miami', 'Milan', 'Moscow', 'Munich'],
    N: ['Norway', 'New York', 'New Zealand', 'Nigeria', 'Naples'],
    O: ['Oslo', 'Orlando', 'Ottawa', 'Oman'],
    P: ['Paris', 'Portugal', 'Poland', 'Prague', 'Peru', 'Philadelphia'],
    R: ['Rome', 'Russia', 'Rio de Janeiro', 'Rotterdam'],
    S: ['Spain', 'Sydney', 'Sweden', 'Seoul', 'Switzerland', 'Singapore'],
    T: ['Tokyo', 'Toronto', 'Turkey', 'Thailand', 'Texas'],
    U: ['United States', 'United Kingdom', 'Uruguay', 'Ukraine'],
    V: ['Venice', 'Vienna', 'Vietnam', 'Vancouver'],
    W: ['Washington', 'Warsaw', 'Wales', 'Wellington']
  },
  job: {
    A: ['Actor', 'Architect', 'Artist', 'Accountant', 'Astronaut', 'Author'],
    B: ['Baker', 'Barber', 'Biologist', 'Builder', 'Banker'],
    C: ['Chef', 'Carpenter', 'Cashier', 'Coach', 'Consultant'],
    D: ['Doctor', 'Dentist', 'Driver', 'Detective', 'Designer'],
    E: ['Engineer', 'Electrician', 'Economist', 'Editor'],
    F: ['Firefighter', 'Farmer', 'Fisherman', 'Flight Attendant'],
    G: ['Gardener', 'Geologist', 'Graphic Designer', 'Guide'],
    H: ['Hairdresser', 'Historian', 'Hunter', 'Hotelier'],
    I: ['Instructor', 'Inspector', 'Illustrator', 'Interpreter'],
    J: ['Journalist', 'Judge', 'Jeweler', 'Janitor'],
    K: ['Karate Teacher'],
    L: ['Lawyer', 'Librarian', 'Lifeguard', 'Lecturer'],
    M: ['Musician', 'Mechanic', 'Manager', 'Model', 'Mailman'],
    N: ['Nurse', 'Nutritionist', 'Nanny', 'Navigator'],
    O: ['Optician', 'Officer', 'Operator', 'Orthodontist'],
    P: ['Pilot', 'Police Officer', 'Photographer', 'Plumber', 'Professor'],
    R: ['Reporter', 'Researcher', 'Radiologist', 'Realtor'],
    S: ['Scientist', 'Singer', 'Surgeon', 'Sailor', 'Soldier', 'Student'],
    T: ['Teacher', 'Technician', 'Tailor', 'Translator', 'Therapist'],
    U: ['Umpire', 'Underwriter'],
    V: ['Veterinarian', 'Violinist', 'Videographer'],
    W: ['Waiter', 'Writer', 'Welder', 'Web Developer']
  },
  object: {
    A: ['Airplane', 'Anchor', 'Apron', 'Album', 'Arrow'],
    B: ['Book', 'Bottle', 'Bag', 'Ball', 'Bed', 'Bicycle', 'Box'],
    C: ['Chair', 'Clock', 'Camera', 'Computer', 'Cup', 'Car'],
    D: ['Door', 'Desk', 'Diamond', 'Doll', 'Dice'],
    E: ['Envelope', 'Eraser', 'Engine', 'Earrings'],
    F: ['Fork', 'Fan', 'Flag', 'Flashlight', 'Frame'],
    G: ['Guitar', 'Glasses', 'Globe', 'Gun', 'Glove'],
    H: ['Hat', 'Hammer', 'Helmet', 'Hook', 'Harp'],
    I: ['Iron', 'Ink', 'Island map'],
    J: ['Jacket', 'Jar', 'Jewel', 'Jug'],
    K: ['Key', 'Knife', 'Kettle', 'Kite'],
    L: ['Lamp', 'Ladder', 'Lock', 'Laptop', 'Leaf'],
    M: ['Mirror', 'Map', 'Magnet', 'Mask', 'Mug'],
    N: ['Needle', 'Notebook', 'Necklace', 'Nut'],
    O: ['Oven', 'Oil lamp', 'Organ'],
    P: ['Pen', 'Pencil', 'Phone', 'Plate', 'Pillow', 'Piano'],
    R: ['Ring', 'Radio', 'Rope', 'Ruler', 'Robot'],
    S: ['Shoe', 'Spoon', 'Shirt', 'Scissors', 'Soap', 'Sword'],
    T: ['Table', 'Telephone', 'Towel', 'Television', 'Ticket', 'Toothbrush'],
    U: ['Umbrella', 'Uniform'],
    V: ['Vase', 'Violin', 'Vehicle'],
    W: ['Watch', 'Window', 'Wallet', 'Wheel', 'Whistle']
  },
  verb: {
    A: ['Ask', 'Agree', 'Accept', 'Answer', 'Arrive', 'Apply'],
    B: ['Build', 'Buy', 'Bake', 'Bring', 'Begin', 'Breathe'],
    C: ['Call', 'Clean', 'Cook', 'Catch', 'Choose', 'Climb'],
    D: ['Dance', 'Draw', 'Drink', 'Drive', 'Decide', 'Discover'],
    E: ['Eat', 'Explain', 'Enjoy', 'Explore', 'Enter', 'Escape'],
    F: ['Fly', 'Find', 'Feel', 'Fight', 'Follow', 'Forget'],
    G: ['Give', 'Go', 'Grow', 'Grab', 'Guess', 'Greet'],
    H: ['Help', 'Hear', 'Hope', 'Hold', 'Hide', 'Hug'],
    I: ['Imagine', 'Invite', 'Improve', 'Inform', 'Inspire'],
    J: ['Jump', 'Join', 'Joke', 'Judge', 'Jog'],
    K: ['Know', 'Kick', 'Kiss', 'Keep', 'Knock'],
    L: ['Listen', 'Learn', 'Look', 'Love', 'Laugh', 'Leave'],
    M: ['Make', 'Meet', 'Move', 'Manage', 'Mix', 'Mention'],
    N: ['Notice', 'Need', 'Nod', 'Name', 'Navigate'],
    O: ['Open', 'Order', 'Organize', 'Observe', 'Offer'],
    P: ['Play', 'Paint', 'Plan', 'Push', 'Pull', 'Protect'],
    R: ['Run', 'Read', 'Remember', 'Reach', 'Ride', 'Relax'],
    S: ['Speak', 'See', 'Sing', 'Smile', 'Sleep', 'Study', 'Swim'],
    T: ['Think', 'Talk', 'Travel', 'Teach', 'Try', 'Tell'],
    U: ['Understand', 'Use', 'Unlock', 'Upgrade'],
    V: ['Visit', 'Vote', 'View', 'Validate', 'Volunteer'],
    W: ['Walk', 'Watch', 'Write', 'Wait', 'Win', 'Wash']
  },
  adjective: {
    A: ['Active', 'Angry', 'Awesome', 'Ancient', 'Anxious', 'Ambitious'],
    B: ['Brave', 'Bright', 'Big', 'Beautiful', 'Busy', 'Bitter'],
    C: ['Calm', 'Cold', 'Cute', 'Clever', 'Creative', 'Confident'],
    D: ['Dark', 'Dangerous', 'Delightful', 'Dry', 'Deep'],
    E: ['Easy', 'Energetic', 'Early', 'Expensive', 'Excited'],
    F: ['Fast', 'Funny', 'Friendly', 'Famous', 'Fresh', 'Fierce'],
    G: ['Good', 'Great', 'Generous', 'Gentle', 'Golden', 'Gorgeous'],
    H: ['Happy', 'Hot', 'Heavy', 'Honest', 'Healthy', 'Huge'],
    I: ['Important', 'Intelligent', 'Interesting', 'Incredible'],
    J: ['Joyful', 'Juicy', 'Jolly', 'Jealous'],
    K: ['Kind', 'Keen', 'Knowledgeable'],
    L: ['Lucky', 'Loud', 'Little', 'Lazy', 'Lovely', 'Light'],
    M: ['Modern', 'Magic', 'Mysterious', 'Mad', 'Mighty'],
    N: ['Nice', 'New', 'Neat', 'Nervous', 'Natural'],
    O: ['Old', 'Optimistic', 'Open', 'Original'],
    P: ['Polite', 'Powerful', 'Pretty', 'Proud', 'Patient', 'Peaceful'],
    R: ['Rich', 'Rare', 'Ready', 'Real', 'Reliable', 'Rapid'],
    S: ['Strong', 'Smart', 'Sweet', 'Slow', 'Simple', 'Silent'],
    T: ['Tall', 'Tiny', 'Tired', 'Tasty', 'Tender', 'True'],
    U: ['Unique', 'Useful', 'Urgent', 'Unusual'],
    V: ['Vibrant', 'Valuable', 'Victorious', 'Vast'],
    W: ['Warm', 'Wise', 'Wild', 'Wonderful', 'Weak', 'Wicked']
  },
  color: {
    A: ['Amber', 'Aqua', 'Azure', 'Amethyst'],
    B: ['Blue', 'Black', 'Brown', 'Beige', 'Bronze'],
    C: ['Crimson', 'Cyan', 'Coral', 'Charcoal', 'Copper'],
    D: ['Dark Blue', 'Denim'],
    E: ['Emerald', 'Ebony'],
    F: ['Fuchsia', 'Forest Green'],
    G: ['Green', 'Gold', 'Gray', 'Grey', 'Goldenrod'],
    H: ['Hazel', 'Hot Pink'],
    I: ['Indigo', 'Ivory'],
    J: ['Jade'],
    K: ['Khaki'],
    L: ['Lime', 'Lavender', 'Lilac', 'Lemon'],
    M: ['Magenta', 'Maroon', 'Mint', 'Mustard'],
    N: ['Navy', 'Neon Green'],
    O: ['Orange', 'Olive', 'Ochre'],
    P: ['Purple', 'Pink', 'Peach', 'Plum'],
    R: ['Red', 'Rose', 'Ruby', 'Rust'],
    S: ['Silver', 'Scarlet', 'Salmon', 'Sapphire'],
    T: ['Turquoise', 'Teal', 'Tan'],
    U: ['Ultramarine'],
    V: ['Violet', 'Vanilla'],
    W: ['White', 'Wine']
  },
  sport: {
    A: ['Archery', 'Athletics', 'Aikido', 'Aerobics'],
    B: ['Baseball', 'Basketball', 'Badminton', 'Boxing', 'Bowling'],
    C: ['Cricket', 'Cycling', 'Curling', 'Climbing', 'Canoeing'],
    D: ['Diving', 'Darts', 'Dodgeball', 'Drifting'],
    E: ['Equestrian', 'Esports'],
    F: ['Football', 'Fencing', 'Figure Skating', 'Fishing'],
    G: ['Golf', 'Gymnastics', 'Grappling'],
    H: ['Hockey', 'Handball', 'Hiking', 'Horse Racing'],
    I: ['Ice Hockey', 'Ice Skating', 'Inline Skating'],
    J: ['Judo', 'Jiu-Jitsu', 'Jogging', 'Javelin'],
    K: ['Karate', 'Kayaking', 'Kickboxing', 'Kite Surfing'],
    L: ['Lacrosse', 'Long Jump', 'Luge'],
    M: ['Marathon', 'Motorsport', 'Mountaineering', 'MMA'],
    N: ['Netball', 'Nordic Walking'],
    O: ['Orienteering', 'Obstacle Course'],
    P: ['Polo', 'Parkour', 'Paddleboarding', 'Powerlifting'],
    R: ['Rugby', 'Rowing', 'Running', 'Racquetball'],
    S: ['Soccer', 'Swimming', 'Surfing', 'Skiing', 'Snowboarding', 'Skateboarding'],
    T: ['Tennis', 'Table Tennis', 'Track and Field', 'Taekwondo', 'Triathlon'],
    U: ['Ultimate Frisbee', 'Underwater Hockey'],
    V: ['Volleyball', 'Vaulting'],
    W: ['Water Polo', 'Wrestling', 'Weightlifting', 'Wakeboarding', 'Windsurfing']
  },
  clothing: {
    A: ['Anorak', 'Apron', 'Ascot'],
    B: ['Boots', 'Belt', 'Blouse', 'Blazer', 'Beret', 'Beanie', 'Bikini'],
    C: ['Coat', 'Cap', 'Cardigan', 'Cloak', 'Corset', 'Chinos'],
    D: ['Dress', 'Denim jacket', 'Dungarees'],
    E: ['Earrings', 'Earmuffs', 'Evening gown'],
    F: ['Fedora', 'Flannel shirt', 'Flip-flops', 'Fleece'],
    G: ['Gloves', 'Glasses', 'Gown', 'Goggles', 'Galoshes'],
    H: ['Hat', 'Hoodie', 'Headband', 'High heels'],
    I: ['Infinity scarf', 'Insulated jacket'],
    J: ['Jacket', 'Jeans', 'Jumper', 'Jumpsuit', 'Jewelry'],
    K: ['Kilt', 'Kimono', 'Knee-high socks'],
    L: ['Leggings', 'Loafers', 'Leather jacket', 'Leotard'],
    M: ['Mitten', 'Muffler', 'Moccasins', 'Miniskirt'],
    N: ['Necktie', 'Nightgown', 'Necklace'],
    O: ['Overalls', 'Overcoat', 'Oxford shoes'],
    P: ['Pants', 'Parka', 'Polo shirt', 'Poncho', 'Pyjamas', 'Pullover'],
    R: ['Raincoat', 'Robe', 'Ring', 'Running shoes'],
    S: ['Shirt', 'Shoes', 'Skirt', 'Sweater', 'Socks', 'Suit', 'Scarf', 'Shorts'],
    T: ['T-shirt', 'Trousers', 'Tie', 'Trench coat', 'Turtleneck', 'Tuxedo', 'Tights'],
    U: ['Underwear', 'Uniform', 'Undershirt'],
    V: ['Vest', 'Veil', 'V-neck sweater', 'Visor'],
    W: ['Watch', 'Windbreaker', 'Wedge shoes', 'Wetsuit', 'Waistcoat']
  },
  body_part: {
    A: ['Arm', 'Ankle', 'Abdomen', 'Artery', 'Armpit', 'Appendix'],
    B: ['Brain', 'Back', 'Bone', 'Blood', 'Belly', 'Bladder'],
    C: ['Chest', 'Cheek', 'Chin', 'Calf', 'Collarbone'],
    D: ['Diaphragm'],
    E: ['Eye', 'Ear', 'Elbow', 'Eyebrow', 'Eyelash', 'Eardrum'],
    F: ['Foot', 'Finger', 'Face', 'Forehead', 'Femur'],
    G: ['Gums', 'Groin', 'Gallbladder'],
    H: ['Hand', 'Heart', 'Head', 'Hair', 'Heel', 'Hip'],
    I: ['Intestine', 'Iris', 'Index finger'],
    J: ['Jaw', 'Joint', 'Jugular'],
    K: ['Knee', 'Kidney', 'Knuckle', 'Kneecap'],
    L: ['Leg', 'Lip', 'Liver', 'Lung', 'Larynx'],
    M: ['Mouth', 'Muscle', 'Mustache', 'Molar'],
    N: ['Nose', 'Neck', 'Nail', 'Nerve', 'Nostril', 'Navel'],
    O: ['Optic nerve', 'Organ'],
    P: ['Palm', 'Pelvis', 'Pancreas', 'Pharynx'],
    R: ['Rib', 'Ribcage', 'Radius', 'Retina'],
    S: ['Stomach', 'Shoulder', 'Skin', 'Spine', 'Skull', 'Shin'],
    T: ['Toe', 'Throat', 'Tongue', 'Tooth', 'Thigh', 'Thumb', 'Tendon'],
    U: ['Uvula', 'Ulna'],
    V: ['Vein', 'Vertebra', 'Vocal cords'],
    W: ['Wrist', 'Waist', 'Windpipe']
  },
  musical_instrument: {
    A: ['Accordion', 'Acoustic Guitar', 'Autoharp'],
    B: ['Bass', 'Banjo', 'Bassoon', 'Bongos', 'Bagpipes', 'Bugle'],
    C: ['Clarinet', 'Cello', 'Cymbals', 'Congas', 'Cornet', 'Chimes'],
    D: ['Drums', 'Drum kit', 'Double bass', 'Didgeridoo'],
    E: ['Electric Guitar', 'Electronic Keyboard', 'Euphonium', 'English Horn'],
    F: ['Flute', 'French Horn', 'Fiddle'],
    G: ['Guitar', 'Gong', 'Glockenspiel'],
    H: ['Harp', 'Harmonica', 'Harpsichord'],
    I: ['Irish Flute'],
    J: ['Jew\'s Harp'],
    K: ['Keyboard', 'Kazoo', 'Koto'],
    L: ['Lute', 'Lyre'],
    M: ['Mandolin', 'Marimba', 'Melodica', 'Microphone'],
    N: ['Ney'],
    O: ['Oboe', 'Organ', 'Ocarina'],
    P: ['Piano', 'Piccolo', 'Pan Flute'],
    R: ['Recorder', 'Rattle'],
    S: ['Saxophone', 'Snare Drum', 'Synthesizer', 'Sitar'],
    T: ['Trumpet', 'Trombone', 'Tuba', 'Tambourine', 'Timpani', 'Triangle'],
    U: ['Ukulele'],
    V: ['Violin', 'Viola', 'Vibraphone'],
    W: ['Whistle', 'Woodblock']
  },
  plant_flower: {
    A: ['Aloe', 'Aster', 'Azalea', 'Acacia', 'Apple Tree'],
    B: ['Bamboo', 'Begonia', 'Birch', 'Bonsai', 'Bluebell', 'Buttercup'],
    C: ['Cactus', 'Carnation', 'Cedar', 'Chrysanthemum', 'Clover', 'Cherry Blossom'],
    D: ['Daisy', 'Daffodil', 'Dahlia', 'Dandelion'],
    E: ['Eucalyptus', 'Edelweiss', 'Elm'],
    F: ['Fern', 'Ficus', 'Foxglove', 'Freesia', 'Fir'],
    G: ['Geranium', 'Gardenia', 'Gladiolus'],
    H: ['Hibiscus', 'Hydrangea', 'Hyacinth', 'Heather'],
    I: ['Iris', 'Ivy'],
    J: ['Jasmine', 'Juniper'],
    K: ['Kudzu'],
    L: ['Lily', 'Lavender', 'Lotus', 'Lilac', 'Lupin'],
    M: ['Magnolia', 'Marigold', 'Maple', 'Mint', 'Moss'],
    N: ['Narcissus', 'Nettle'],
    O: ['Orchid', 'Oak', 'Oleander'],
    P: ['Peony', 'Petunia', 'Pine', 'Poppy', 'Palm'],
    R: ['Rose', 'Rhododendron', 'Rosemary'],
    S: ['Sunflower', 'Snapdragon', 'Sage', 'Sycamore', 'Snowdrop'],
    T: ['Tulip', 'Thistle', 'Thyme'],
    U: ['Umbrella Plant'],
    V: ['Violet', 'Verbena', 'Venus Flytrap'],
    W: ['Willow', 'Water Lily', 'Wisteria']
  },
  vehicle: {
    A: ['Ambulance', 'Airplane', 'Airship'],
    B: ['Bicycle', 'Bus', 'Boat', 'Barge', 'Bulldozer'],
    C: ['Car', 'Canoe', 'Cruiser', 'Caravan', 'Convertible'],
    D: ['Drone', 'Dump truck'],
    E: ['Electric car', 'Estate car'],
    F: ['Ferry', 'Fire truck', 'Forklift'],
    G: ['Glider', 'Go-kart'],
    H: ['Helicopter', 'Hovercraft', 'Hearse'],
    I: ['Icebreaker'],
    J: ['Jet', 'Jeep', 'Jet ski'],
    K: ['Kayak', 'Kart'],
    L: ['Lorry', 'Limousine', 'Lifeboat'],
    M: ['Motorcycle', 'Minibus', 'Metro', 'Monorail'],
    N: ['Narrowboat'],
    O: ['Ocean liner', 'Omnibus'],
    P: ['Plane', 'Pickup truck', 'Paraglider'],
    R: ['Rowboat', 'Rocket', 'Raft'],
    S: ['Submarine', 'Scooter', 'Ship', 'Sailboat', 'Speedboat', 'Snowmobile'],
    T: ['Train', 'Truck', 'Taxi', 'Tram', 'Tractor'],
    U: ['Unicycle', 'Underground train'],
    V: ['Van', 'Vessel'],
    W: ['Wagon', 'Watercraft']
  },
  school_subject: {
    A: ['Art', 'Algebra', 'Astronomy', 'Anthropology', 'Anatomy'],
    B: ['Biology', 'Botany', 'Business'],
    C: ['Chemistry', 'Calculus', 'Computer Science', 'Civics'],
    D: ['Drama', 'Design'],
    E: ['English', 'Economics', 'Earth Science', 'Ethics'],
    F: ['French', 'Finance', 'Philosophy'],
    G: ['Geography', 'Geometry', 'Geology', 'German'],
    H: ['History', 'Health'],
    I: ['Information Technology', 'Italian'],
    J: ['Journalism', 'Japanese'],
    K: ['Kinesiology'],
    L: ['Literature', 'Law', 'Linguistics', 'Latin'],
    M: ['Math', 'Mathematics', 'Music'],
    N: ['Natural Science', 'Nutrition'],
    O: ['Oceanography'],
    P: ['Physics', 'Psychology', 'Philosophy', 'Physical Education', 'Poetry'],
    R: ['Reading', 'Religion', 'Robotics'],
    S: ['Science', 'Sociology', 'Spanish', 'Statistics'],
    T: ['Technology', 'Theater'],
    U: ['Urban Studies'],
    V: ['Veterinary Medicine', 'Visual Arts'],
    W: ['Writing', 'World History']
  },
  movie_tv: {
    A: ['Avatar', 'Aladdin', 'Avengers', 'Alien', 'Arrow'],
    B: ['Batman', 'Breaking Bad', 'Barbie', 'Braveheart', 'Black Mirror'],
    C: ['Casablanca', 'Chernobyl', 'Cars', 'Cinderella', 'Cobra Kai'],
    D: ['Dune', 'Dexter', 'Dracula', 'Doctor Who', 'Deadpool'],
    E: ['Euphoria', 'Elf', 'Encanto', 'ET'],
    F: ['Friends', 'Frozen', 'Fight Club', 'Fargo', 'Forrest Gump'],
    G: ['Gladiator', 'Game of Thrones', 'Godzilla', 'Glee'],
    H: ['Harry Potter', 'House of Cards', 'Homeland', 'Hulk'],
    I: ['Inception', 'Interstellar', 'Iron Man', 'Invincible'],
    J: ['Joker', 'Jurassic Park', 'Jaws'],
    K: ['Kill Bill', 'King Kong'],
    L: ['Lost', 'Loki', 'Lion King', 'Lord of the Rings'],
    M: ['Matrix', 'Mad Men', 'Mulan', 'Moana', 'Modern Family'],
    N: ['Narcos', 'Nemo', 'New Girl'],
    O: ['Ozark', 'Oppenheimer', 'Office'],
    P: ['Peaky Blinders', 'Pulp Fiction', 'Pokemon', 'Prison Break'],
    R: ['Ratatouille', 'Rocky', 'Rambo', 'Rick and Morty'],
    S: ['Stranger Things', 'Sherlock', 'Seinfeld', 'Star Wars', 'Shrek', 'Suits'],
    T: ['Titanic', 'The Wire', 'The Crown', 'Ted Lasso', 'Twilight'],
    U: ['Umbrella Academy', 'Up'],
    V: ['Vikings', 'Vampire Diaries'],
    W: ['Wednesday', 'Westworld', 'WandaVision', 'Wicked']
  },
  celebrity: {
    A: ['Adele', 'Ariana Grande', 'Albert Einstein', 'Arnold Schwarzenegger'],
    B: ['Beyonce', 'Brad Pitt', 'Bill Gates', 'Barack Obama', 'Bruno Mars'],
    C: ['Cristiano Ronaldo', 'Chris Evans', 'Celine Dion', 'Charles Darwin'],
    D: ['Drake', 'Dua Lipa', 'David Beckham', 'Daniel Radcliffe'],
    E: ['Elon Musk', 'Eminem', 'Ed Sheeran', 'Emma Watson', 'Elvis Presley'],
    F: ['Freddie Mercury', 'Frank Sinatra', 'Floyd Mayweather'],
    G: ['George Clooney', 'Gal Gadot', 'Gordon Ramsay'],
    H: ['Harry Styles', 'Hugh Jackman', 'Harrison Ford'],
    I: ['Isaac Newton', 'Idris Elba'],
    J: ['Justin Bieber', 'Jennifer Lopez', 'Johnny Depp', 'Jeff Bezos', 'Jackie Chan'],
    K: ['Katy Perry', 'Keanu Reeves', 'Kim Kardashian', 'Kanye West'],
    L: ['Leonardo DiCaprio', 'Lady Gaga', 'Lionel Messi', 'LeBron James'],
    M: ['Michael Jackson', 'Madonna', 'Morgan Freeman', 'Marilyn Monroe'],
    N: ['Nelson Mandela', 'Natalie Portman', 'Neil Armstrong'],
    O: ['Oprah Winfrey', 'Orlando Bloom'],
    P: ['Paul McCartney', 'Pedro Pascal', 'Pele'],
    R: ['Rihanna', 'Robert Downey Jr', 'Ryan Reynolds', 'Rafael Nadal'],
    S: ['Shakira', 'Selena Gomez', 'Stephen Hawking', 'Steve Jobs', 'Snoop Dogg'],
    T: ['Taylor Swift', 'Tom Cruise', 'Tom Hanks', 'Tiger Woods'],
    U: ['Usain Bolt'],
    V: ['Vincent van Gogh', 'Vin Diesel'],
    W: ['Will Smith', 'Walt Disney', 'Whitney Houston']
  },
  brand: {
    A: ['Apple', 'Amazon', 'Adidas', 'Audi', 'Adobe'],
    B: ['BMW', 'Burger King', 'Boeing', 'Balenciaga'],
    C: ['Coca-Cola', 'Chanel', 'Canon', 'Chevrolet', 'Cartier'],
    D: ['Disney', 'Dell', 'Dior', 'Doritos', 'Domino\'s'],
    E: ['eBay', 'Electronic Arts', 'Epson'],
    F: ['Ford', 'Ferrari', 'Fendi', 'FedEx', 'Facebook'],
    G: ['Google', 'Gucci', 'Gillette', 'Gap'],
    H: ['Honda', 'Hyundai', 'Heineken', 'Hermes', 'HP'],
    I: ['IBM', 'Intel', 'IKEA', 'Instagram'],
    J: ['Jordan', 'Jeep', 'Jaguar'],
    K: ['KFC', 'Kellogg\'s', 'Kia'],
    L: ['Lego', 'Louis Vuitton', 'L\'Oreal', 'Lamborghini'],
    M: ['Microsoft', 'McDonald\'s', 'Mercedes', 'Meta', 'Monster'],
    N: ['Nike', 'Netflix', 'Nintendo', 'Nestle', 'Nvidia'],
    O: ['Oracle', 'Oreo'],
    P: ['Pepsi', 'Porsche', 'Puma', 'Prada', 'PlayStation', 'Panasonic'],
    R: ['Rolex', 'Red Bull', 'Ray-Ban', 'Renault'],
    S: ['Samsung', 'Sony', 'Starbucks', 'Subway', 'Spotify', 'Sephora'],
    T: ['Tesla', 'Toyota', 'Target', 'Tiffany', 'Twitter'],
    U: ['Uber', 'Under Armour', 'Uniqlo'],
    V: ['Visa', 'Volkswagen', 'Volvo', 'Versace'],
    W: ['Walmart', 'Warner Bros', 'Wendy\'s']
  }
};

/**
 * Returns 3-5 example English words for a category starting with the specified letter.
 */
export function getCategorySuggestions(letter: string, categoryId: string): string[] {
  const cleanLetter = (letter || '').trim().toUpperCase();
  const categoryDict = CATEGORY_VOCABULARY[categoryId];
  if (categoryDict && categoryDict[cleanLetter] && categoryDict[cleanLetter].length > 0) {
    return categoryDict[cleanLetter].slice(0, 4);
  }

  // Fallback defaults if obscure letter
  return [];
}
